import React from "react";
import { Box, Typography, Paper, useTheme } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";

interface GoalDistributionProps {
  title: string;
  subtitle: string;
  data: {
    CPM: number;
    CPC: number;
    CPV: number;
  };
  isLoading?: boolean;
}

const GoalDistributionChart: React.FC<GoalDistributionProps> = ({
  title,
  subtitle,
  data,
  isLoading = false,
}) => {
  const theme = useTheme();

  // Transform the data for the bar chart and ensure values are in decimal format (0-1)
  // The API might return values as percentages (e.g., 75) or decimals (e.g., 0.75)
  const normalizeValue = (value: number): number => {
    // If value is greater than 1, assume it's a percentage and convert to decimal
    return value > 1 ? value / 100 : value;
  };

  const chartData = [
    {
      name: "CPM",
      value: normalizeValue(data.CPM),
    },
    {
      name: "CPC",
      value: normalizeValue(data.CPC),
    },
    {
      name: "CPV",
      value: normalizeValue(data.CPV),
    },
  ];

  // Colors for the bars
  const COLORS = [
    theme.palette.primary.main,
    theme.palette.info.main,
    theme.palette.neutral[500],
  ];

  // Custom tooltip
  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ name: string; value: number }>;
  }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            padding: "10px",
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: "4px",
          }}
        >
          <Typography variant="body2" color="textPrimary">
            {`${payload[0].name}: ${Math.round(payload[0].value * 100)}%`}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // No need to calculate total as we're displaying the raw percentages

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: "100%",
        borderRadius: "8px",
        border: `1px solid ${theme.palette.grey[200]}`,
        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
      }}
    >
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {subtitle}
      </Typography>

      <Box sx={{ width: "100%", height: 250 }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography>Loading...</Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{
                top: 20,
                right: 70, // Further increased right margin to accommodate 100% labels
                left: 20,
                bottom: 5,
              }}
            >
              <XAxis
                type="number"
                domain={[0, 1]}
                tickFormatter={(value) => `${Math.round(value * 100)}%`}
              />
              <YAxis type="category" dataKey="name" />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" barSize={30}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
                <LabelList
                  dataKey="value"
                  position="right"
                  formatter={(value: number) => `${Math.round(value * 100)}%`}
                  offset={10} // Add offset to move labels away from the bars
                  style={{ textAnchor: "start" }} // Ensure text alignment is consistent
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Paper>
  );
};

export default GoalDistributionChart;
